/**
 * The API answers in English; the site is French.
 *
 * Validation messages and refusals are written once, on the backend, where the
 * rule lives — so they arrive in the language the API speaks. Rather than a
 * second copy of the rules here, this is a lookup: the message the API sent,
 * said in French. A message with no entry is never shown in English; it falls
 * back to a plain sentence for that field, and the original is logged.
 *
 * Its counterpart is spread across the request DTOs, the Assert constraints
 * and the refusals the domain itself writes (`Provider`, `Post`, `Booking` and
 * friends); a message reworded there needs a line changed here. The newer
 * parts of the backend write their refusals in French already — those pass
 * through untouched.
 */

const EXACT = new Map([
  // --- Which city ---------------------------------------------------------
  [
    'Several cities are open: sign in, or say which one with the "city" parameter',
    'Plusieurs villes sont ouvertes : choisissez la vôtre, ou connectez-vous.',
  ],

  // --- Signing up and signing in ------------------------------------------
  ['Username is required', "Choisissez un nom d'utilisateur"],
  [
    'A username may contain letters, digits, spaces, apostrophes, dots, dashes and underscores. It must start with a letter or a digit, and a separator must be followed by one.',
    'Lettres, chiffres, espaces, apostrophes, points, tirets et tirets bas. À commencer par une lettre ou un chiffre.',
  ],
  ['Username contains an invisible character', 'Ce nom contient un caractère invisible. Retapez-le plutôt que de le coller.'],
  ['This username is already taken.', "Ce nom d'utilisateur est déjà pris"],
  ['Email is required', 'Entrez votre adresse email'],
  ['Invalid email format', "Cette adresse email n'est pas valide"],
  ['This email is already used.', 'Un compte existe déjà avec cette adresse email'],
  ['Password is required', 'Choisissez un mot de passe'],
  ['You must accept the terms of use', 'Acceptez les conditions d’utilisation pour créer votre compte'],
  ['Invalid credentials', 'Email ou mot de passe incorrect'],
  ['This account is suspended', 'Ce compte est suspendu'],
  ['This endpoint requires an authenticated user', 'Connectez-vous pour continuer'],
  ['User not found', 'Compte introuvable'],

  // --- Their own account ---------------------------------------------------
  [
    'Your current password is required to change your email address or your password',
    'Votre mot de passe actuel est nécessaire pour changer votre adresse email ou votre mot de passe',
  ],
  ['This is not your current password', "Ce n'est pas votre mot de passe actuel"],

  // --- The feed and its comments ------------------------------------------
  ['A post needs a title', 'Donnez un titre à votre publication'],
  ['A post needs a body', 'Écrivez votre message'],
  ['A comment cannot be empty', 'Écrivez votre réponse'],
  ['This post is no longer open for comments', "Cette publication n'accepte plus de commentaires"],

  // --- The agenda ----------------------------------------------------------
  ['An event needs a title', 'Donnez un titre à votre événement'],
  ['An event cannot end before it starts', 'La fin doit être après le début'],

  // --- Reviews -------------------------------------------------------------
  ['A provider cannot review their own listing', 'Vous ne pouvez pas laisser un avis sur votre propre fiche'],
  ['Reviewing requires an authenticated user', 'Connectez-vous pour laisser un avis'],
  ['A rating is required', 'Donnez une note'],

  // --- A professional's listing -------------------------------------------
  ['Category is required', 'Choisissez un métier'],
  ['Provider name is required', 'Indiquez le nom de votre activité'],
  ['Provider name cannot be empty', 'Indiquez le nom de votre activité'],
  ['Invalid website URL', "Cette adresse web n'est pas valide (https://…)"],
  ['Invalid logo URL', "L'adresse de ce logo n'est pas valide (https://…)"],
  ['Postal code must be five digits', 'Un code postal s’écrit en cinq chiffres'],
  ['You have no listing to submit yet', "Vous n'avez pas encore de fiche à envoyer"],
  ['No city is open yet', "Aucune ville n'est encore ouverte"],

  // --- What a professional sells, and when ---------------------------------
  ['An offering needs a name', 'Donnez un nom à cette prestation'],
  ['An offering on quote cannot carry an amount', 'Une prestation sur devis ne porte pas de prix'],
  ['A slot must end after it starts', 'La fin doit être après le début'],
  ['An exception must end after it starts', 'La fin doit être après le début'],
  ['The offering does not belong to this provider', "Cette prestation n'est pas la vôtre"],

  // --- Appointments --------------------------------------------------------
  ['An appointment that has already started cannot be cancelled', "Un rendez-vous déjà commencé ne peut plus être annulé"],
  ['An appointment cannot be completed before it has ended', "Un rendez-vous ne peut pas être marqué honoré avant sa fin"],
  ['A reason is required to decline a request', 'Indiquez un motif de refus'],

  // --- The upkeep list -----------------------------------------------------
  ['A task needs a title', 'Donnez un nom à cette tâche'],

  // --- Moderation, as a resident sees it -----------------------------------
  ['A reason is required to hide content', 'Indiquez un motif'],
  ['A reason is required to refuse a listing', 'Indiquez un motif de refus'],
  ['A reason is required to refuse a review', 'Indiquez un motif de refus'],
  ['Content type is required', 'Type de contenu requis'],
  ['Invalid content type', 'Type de contenu invalide'],

  // --- Anything the form did not put there, such as a smuggled "roles" ------
  ['This field is not allowed.', "Ce champ n'est pas accepté"],

  // --- Symfony's own defaults, for constraints that carry no message of ours
  ['This value should not be blank.', 'Ce champ est obligatoire'],
  ['This value should not be null.', 'Ce champ est obligatoire'],
  ['This value is not valid.', "Cette valeur n'est pas acceptée"],
  ['This value is not a valid email address.', "Cette adresse email n'est pas valide"],
  ['This value is not a valid URL.', "Cette adresse web n'est pas valide"],
  ['This value is not a valid UUID.', 'Référence invalide'],
  ['This value should be of type string.', 'Ce champ attend du texte'],
  ['This value should be of type bool.', 'Ce champ attend oui ou non'],
  ['This value should be of type int.', 'Ce champ attend un nombre'],
  ['This value should be positive.', 'Ce nombre doit être positif'],
  ['This collection should contain 1 element or more.', 'Choisissez au moins un élément'],

  // --- The headline of a 422, shown when no field carries the problem -------
  ['Validation failed', 'Vérifiez les informations saisies'],

  // --- Written in French by the frontend itself when a request never landed -
  ['Inscription impossible', 'Inscription impossible'],
  ['Connexion impossible', 'Connexion impossible'],
]);

/** Messages that carry a number, which has to come through the translation. */
const PATTERNS = [
  [/ must be at least (\d+) characters$/, (n) => `Au moins ${n} caractères`],
  [/ cannot be longer than (\d+) characters/, (n) => `Au plus ${n} caractères`],
  [/^Username must be between \d+ and (\d+) characters/, (n) => `Au plus ${n} caractères`],
  [/^A SIRET is (\d+) digits/, (n) => `Un SIRET compte ${n} chiffres`],
  [/^(?:The r|R)ating must be between \d+ and (\d+)/, (n) => `Donnez une note de 1 à ${n}`],
  [/^A duration must be a multiple of (\d+) minutes/, (n) => `Une durée se règle par tranches de ${n} minutes`],
  [/^A duration must be between (\d+) and (\d+) minutes/, (min, max) => `Une durée est comprise entre ${min} et ${max} minutes`],
  [/^An amount cannot exceed (\d+) cents/, (n) => `Le prix ne peut pas dépasser ${(Number(n) / 100).toLocaleString('fr-FR')} €`],
];

/**
 * Refusals the model words around a name, a status or a value: matched on what
 * they say rather than looked up whole.
 */
const DOMAIN_PATTERNS = [
  // A listing being written, submitted or published
  [/cannot be submitted without the legal name/, 'Indiquez la raison sociale de votre entreprise avant de demander la validation'],
  [/cannot be submitted without a SIRET/, 'Indiquez votre SIRET avant de demander la validation'],
  [/cannot be submitted from status/, 'Cette fiche ne peut pas être envoyée en validation dans son état actuel'],
  [/is archived and can no longer be edited/, 'Cette fiche est archivée et ne peut plus être modifiée'],
  [/^This SIRET fails its check digit/, 'Ce SIRET est invalide : vérifiez les chiffres saisis'],
  [/^Invalid French phone number/, "Ce numéro de téléphone n'est pas valide"],
  [/^Invalid postal code/, "Ce code postal n'est pas valide"],
  [/^Invalid username format/, 'Lettres, chiffres, espaces, apostrophes, points, tirets et tirets bas. À commencer par une lettre ou un chiffre.'],

  // Geography
  [/does not belong to city/, 'Ce quartier n’appartient pas à cette ville'],
  [/^Neighborhood "[^"]*" not found/, 'Ce quartier est introuvable'],
  [/^City "[^"]*" not found/, 'Cette ville est introuvable'],
  [/^Category "[^"]*" not found/, 'Cette catégorie est introuvable'],
  [/^Provider "[^"]*" not found/, 'Cette fiche est introuvable'],

  // Appointments, whose refusals name the status they are in
  [/^Only a pending request can be (confirmed|declined)/, 'Cette demande a déjà reçu une réponse'],
  [/^Only a confirmed appointment can be completed/, 'Seul un rendez-vous confirmé peut être marqué comme honoré'],
  [/^This booking is already/, 'Ce rendez-vous a déjà été traité'],

  // What a professional sells, and when they work
  [/needs an amount$/, 'Indiquez un prix pour cette prestation'],
  [/^An amount cannot be negative/, 'Le prix ne peut pas être négatif'],
  [/^A time of day must be between/, 'Heure invalide'],
  [/^An availability must end after it starts/, 'Une plage doit se terminer après son début'],

  // Reports
  [/^Invalid target uuid/, 'Contenu introuvable'],
];

/**
 * Said plainly, for a message nobody has translated yet: the field is the one
 * thing we still know for certain.
 */
const FALLBACK_BY_FIELD = {
  username: "Ce nom d'utilisateur n'est pas accepté",
  email: "Cette adresse email n'est pas acceptée",
  password: "Ce mot de passe n'est pas accepté",
  currentPassword: "Ce mot de passe n'est pas accepté",
  newPassword: "Ce mot de passe n'est pas accepté",
  categoryUuid: "Ce métier n'est pas accepté",
  name: "Ce nom n'est pas accepté",
  title: "Ce titre n'est pas accepté",
  body: "Ce message n'est pas accepté",
  phone: "Ce numéro n'est pas accepté",
  contactEmail: "Cette adresse email n'est pas acceptée",
  website: "Cette adresse web n'est pas acceptée",
  postalCode: "Ce code postal n'est pas accepté",
  legalName: "Cette raison sociale n'est pas acceptée",
  siret: "Ce SIRET n'est pas accepté",
  rating: "Cette note n'est pas acceptée",
  citySlug: "Cette ville n'est pas acceptée",
  neighborhoodUuid: "Ce quartier n'est pas accepté",
};

/** For a message carried by no field: the banner. */
const FALLBACK = 'Une erreur est survenue. Réessayez.';

/**
 * Words an English sentence can hardly avoid.
 *
 * The test is for English rather than for French, because guessing French
 * wrongly is the costly mistake: "Trop tard." carries no accent and no
 * article, and replacing it with a generic sentence would lose a message the
 * backend wrote for the reader. An English message that slips through is
 * caught by the warning below and gets a line in the table.
 */
const LOOKS_ENGLISH = /\b(the|is|are|was|not|cannot|can|must|should|required|this|that|your|you|their|already|invalid|found|be|and|or|with|from|has|have|needs|only|one|between|than|longer)\b/i;

/**
 * Translates one message. `field` only steers the fallback.
 */
export function translateApiMessage(message, field) {
  if (!message) {
    return message;
  }

  const exact = EXACT.get(message);

  if (exact) {
    return exact;
  }

  for (const [pattern, build] of PATTERNS) {
    const match = pattern.exec(message);

    if (match) {
      return build(...match.slice(1));
    }
  }

  for (const [pattern, french] of DOMAIN_PATTERNS) {
    if (pattern.test(message)) {
      return french;
    }
  }

  // Not English, so already readable: the newer half of the backend writes its
  // refusals in French, and so does the frontend when a request never landed.
  if (!LOOKS_ENGLISH.test(message)) {
    return message;
  }

  console.warn('Untranslated API message:', message);

  return FALLBACK_BY_FIELD[field] ?? FALLBACK;
}

/**
 * Translates every message of a `violations` object, keys untouched.
 */
export function translateViolations(violations) {
  return Object.fromEntries(
    Object.entries(violations ?? {}).map(([field, message]) => [
      field,
      translateApiMessage(message, field),
    ])
  );
}
